import type { PredictedClient } from '../net/client.js';
import type { NetObjectId, ObjectSnapshot, PresentationEvent } from '../net/protocol.js';
import type { GameFieldDescription } from '../game/field.js';
import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import { DEFAULT_ENEMY_HITBOX_RADIUS_M, type MonsterGrade } from '../sim/archetypes.js';
import { MONSTER_VISUAL_RADIUS_M } from '../reference/entity-sprite-specs-v1.js';
import { PointerAim, worldToScreen, type Camera, type Point, type SkillAimMode, type Viewport } from './input.js';
import { memberPose } from './craft-footprint.js';
import { orbitPosition } from '../sim/orbital-motion.js';
import type { RangePreview, ShapePreview } from './range-preview.js';
import type { FootprintPreview } from './craft-footprint.js';
import { drawSkillAsset, facingRotation, PROCEDURAL_SKILL_VISUALS, type SkillVisuals } from './skill-visuals.js';
import { monsterStatusChips, statusSummaryMarks } from './status-chips.js';

const EFFECT_LIFETIME_MS = 1_500;
const BLAST_EFFECT_LIFETIME_MS = 250;
/** Pure presentation interpolation; radius is supplied exclusively by the server. */
export function blastShockwaveRadiusM(radiusM: number, elapsedMs: number): number {
  return radiusM * Math.max(0, Math.min(1, elapsedMs / BLAST_EFFECT_LIFETIME_MS));
}
/** Fade time of one-shot skill impact and cast art; presentation only. */
const SKILL_FLASH_LIFETIME_MS = 300;
const MAX_EFFECTS = 128;
const WORLD_GRID_SPACING_M = 5;
export const PLAYER_VISUAL_RADIUS_M = PLAYER_RADIUS_M;

/** The drawn body is the replicated hitbox plus the margin a default 0.5m body has today (0.55m). */
const MONSTER_VISUAL_MARGIN_M = MONSTER_VISUAL_RADIUS_M - DEFAULT_ENEMY_HITBOX_RADIUS_M;
/** Body colour per behaviour grade; a monster without a grade is drawn as standard. */
export const MONSTER_GRADE_FILL: Readonly<Record<MonsterGrade, string>> = Object.freeze({ weak: '#f4a98f', standard: '#e76f51', elite: '#9b5de5', boss: '#9d0208' });
/** Elite and boss carry a ring around the body; weak and standard have none. */
export const MONSTER_GRADE_RING: Readonly<Partial<Record<MonsterGrade, { readonly color: string; readonly rings: number }>>> = Object.freeze({
  elite: Object.freeze({ color: '#ffd43b', rings: 1 }), boss: Object.freeze({ color: '#ffd43b', rings: 2 }),
});
export function monsterLook(monster: Extract<ObjectSnapshot, { kind: 'monster' }>): { radiusM: number; fill: string; ring: { readonly color: string; readonly rings: number } | undefined } {
  const grade = monster.grade ?? 'standard';
  return { radiusM: (monster.attackPresentation?.hitboxRadiusM ?? DEFAULT_ENEMY_HITBOX_RADIUS_M) + MONSTER_VISUAL_MARGIN_M, fill: MONSTER_GRADE_FILL[grade], ring: MONSTER_GRADE_RING[grade] };
}
export const CAMERA_ZOOM_VALUES = Object.freeze([1, 2, 3, 4, 5] as const);
const BASE_PIXELS_PER_METRE = 18;
interface Effect { readonly path?: readonly Point[]; readonly createdAt: number; readonly x: number; readonly y: number; readonly label: string; readonly color: string; }
interface BlastEffect { readonly createdAt: number; readonly tick: number; readonly attackKey: string; readonly x: number; readonly y: number; readonly radiusM: number; }
type SkillImpactEvent = Extract<PresentationEvent, { type: 'skill_impact' }>;
type WeaponContactEvent = Extract<PresentationEvent, { type: 'weapon_contact' }>;
/** An instant cone: a skill-owned sector, or a weapon contact that landed in one go (a thrust). Apex, heading, reach, opening. */
interface SectorFlash { readonly createdAt: number; readonly x: number; readonly y: number; readonly facingRad: number; readonly reachM: number; readonly angleDeg: number }
type CastEvent = Extract<PresentationEvent, { type: 'cast' }>;
interface SkillFlash<T> { readonly createdAt: number; readonly event: T }
export type BasicAttackAimMode = 'AUTO_TARGET' | 'POINTER';
export interface RenderRangePreviews { readonly basic?: RangePreview; readonly basicAimMode?: BasicAttackAimMode; readonly skill?: RangePreview; readonly skillAimMode?: SkillAimMode; }

export function visibleGridCoordinates(camera: Camera, viewport: Viewport, spacingM: number): { readonly x: number[]; readonly y: number[] } {
  if (!Number.isFinite(spacingM) || spacingM <= 0) throw new Error('grid spacing must be positive');
  const halfWidthM = viewport.cssWidth / (2 * camera.pixelsPerMetre);
  const halfHeightM = viewport.cssHeight / (2 * camera.pixelsPerMetre);
  const coordinates = (minimum: number, maximum: number): number[] => {
    const values: number[] = [];
    for (let value = Math.ceil(minimum / spacingM) * spacingM; value <= maximum; value += spacingM) values.push(Object.is(value, -0) ? 0 : value);
    return values;
  };
  return {
    x: coordinates(camera.x - halfWidthM, camera.x + halfWidthM),
    y: coordinates(camera.y - halfHeightM, camera.y + halfHeightM),
  };
}

export function basicAttackPreviewAngle(
  local: Extract<ObjectSnapshot, { kind: 'player' }>,
  objects: readonly ObjectSnapshot[],
  aim: Point | undefined,
  mode: BasicAttackAimMode,
): number {
  const aimAngle = (): number | undefined => aim ? Math.atan2(aim.y - local.y, aim.x - local.x) : undefined;
  if (mode === 'POINTER') return aimAngle() ?? (local.facing ? Math.atan2(local.facing.y, local.facing.x) : 0);
  const swing = local.basicAttack?.swingForward;
  if (swing && Number.isFinite(swing.x) && Number.isFinite(swing.y)) return Math.atan2(swing.y, swing.x);
  const nearest = objects.filter((object): object is Extract<ObjectSnapshot, { kind: 'monster' }> => object.kind === 'monster' && object.alive)
    .map((monster) => ({ monster, distance: Math.hypot(monster.x - local.x, monster.y - local.y) }))
    .sort((a, b) => a.distance - b.distance || String(a.monster.netId).localeCompare(String(b.monster.netId)))[0]?.monster;
  if (nearest) return Math.atan2(nearest.y - local.y, nearest.x - local.x);
  if (local.facing) return Math.atan2(local.facing.y, local.facing.x);
  // Without a target or facing, AUTO must never infer aim from the pointer.
  return 0;
}

/**
 * The heading a held skill will be released along, as the server decides it
 * (`releaseAimRad`): the pointer for a pointer-aimed slot; for an automatic
 * one, toward the nearest living monster, else the pointer.
 */
export function skillPreviewAngle(
  local: Extract<ObjectSnapshot, { kind: 'player' }>,
  objects: readonly ObjectSnapshot[],
  aim: Point | undefined,
  mode: SkillAimMode,
): number {
  const pointer = aim ? Math.atan2(aim.y - local.y, aim.x - local.x) : 0;
  if (mode === 'MOUSE') return pointer;
  let nearest: Extract<ObjectSnapshot, { kind: 'monster' }> | undefined; let best = Infinity;
  for (const object of objects) {
    if (object.kind !== 'monster' || !object.alive) continue;
    const distance = Math.hypot(object.x - local.x, object.y - local.y);
    if (distance < best) { best = distance; nearest = object; }
  }
  return nearest !== undefined && best > 1e-9 ? Math.atan2(nearest.y - local.y, nearest.x - local.x) : pointer;
}

/** An authoritative render DTO, independent of the live socket and client
 * prediction. The same GameRenderer code path handles both hosts. */
export interface RendererObjectFrame {
  readonly objects: readonly ObjectSnapshot[];
  readonly localPlayerNetId?: NetObjectId;
  readonly serverTick?: number;
}

export class GameRenderer {
  readonly camera: Camera = { x: 0, y: 0, pixelsPerMetre: 18 };
  private viewport: Viewport = { cssWidth: 1, cssHeight: 1, devicePixelRatio: 1 };
  private readonly context: CanvasRenderingContext2D;
  private effects: Effect[] = [];
  private blasts: BlastEffect[] = [];
  private skillImpacts: SkillFlash<SkillImpactEvent>[] = [];
  private sectorFlashes: SectorFlash[] = [];
  /** Living monsters of the frame being drawn: a skill that re-acquires at release goes to the nearest. */
  private monsters: Point[] = [];
  private skillCasts: SkillFlash<CastEvent>[] = [];
  /** A chain's instant members: a short arc from the enemy each leaves to the one it lands on. */
  private chainLinks: { readonly createdAt: number; readonly from: Point; readonly to: Point }[] = [];
  private aim: Point | undefined;
  /** The held skill slot's aim, read by the preview's ground circles. */
  private skillAimMode: SkillAimMode = 'AUTO';
  private readonly pointerAim = new PointerAim();

  constructor(private readonly canvas: HTMLCanvasElement, private readonly skillVisuals: SkillVisuals = PROCEDURAL_SKILL_VISUALS) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas is unavailable');
    this.context = context;
  }

  resize(): Viewport {
    const bounds = this.canvas.getBoundingClientRect();
    const ratio = Math.max(1, window.devicePixelRatio || 1);
    this.viewport = { cssWidth: Math.max(1, bounds.width), cssHeight: Math.max(1, bounds.height), devicePixelRatio: ratio };
    const width = Math.round(this.viewport.cssWidth * ratio); const height = Math.round(this.viewport.cssHeight * ratio);
    if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; }
    return this.viewport;
  }

  currentViewport(): Viewport { return { ...this.viewport }; }
  setZoom(multiplier: number): void {
    if (!Number.isInteger(multiplier) || !(CAMERA_ZOOM_VALUES as readonly number[]).includes(multiplier)) throw new Error('zoom must be an integer from 1 to 5');
    this.camera.pixelsPerMetre = BASE_PIXELS_PER_METRE * multiplier;
  }
  currentAim(): Point | undefined { return this.pointerAim.worldPoint(this.camera, this.viewport); }
  setAimScreen(point: Point): void { this.pointerAim.setScreenPoint(point); }

  addEvents(events: readonly PresentationEvent[], objects: readonly ObjectSnapshot[], now = performance.now()): void {
    const positions = new Map(objects.map((object) => [object.netId, { x: object.x, y: object.y }]));
    const blastTicks = new Set(events.filter((event) => event.type === 'blast_detonation')
      .map((event) => `${event.tick}:${event.sourceId}`));
    for (const event of events) {
      if (event.type === 'blast_detonation') {
        if (!this.blasts.some((blast) => blast.tick === event.tick && blast.attackKey === event.attackKey)) {
          this.blasts.push({ createdAt: now, tick: event.tick, attackKey: event.attackKey,
            x: event.center.x, y: event.center.y, radiusM: event.radiusM });
        }
      } else if (event.type === 'damage') {
        if (event.hpDamage > 0) {
          const isBlast = event.skillId === 'weapon_basic_attack' && event.sourceId !== undefined
            && blastTicks.has(`${event.tick}:${event.sourceId}`);
          this.effects.push({ createdAt: now, x: event.hitPoint.x, y: event.hitPoint.y,
            label: (isBlast ? '💥 ' : '') + (event.crit ? '치명타 ' : '') + '-' + Math.round(event.hpDamage),
            color: event.crit ? '#ffe066' : isBlast ? '#ffb86c' : '#ff8b8b' });
        } else if (event.shieldAbsorbed > 0) {
          this.effects.push({ createdAt: now, x: event.hitPoint.x, y: event.hitPoint.y,
            label: '보호막 -' + Math.round(event.shieldAbsorbed), color: '#a5d8ff' });
        }
      }
      else if (event.type === 'status_reaction') { const point = positions.get(event.targetNetId); if (point) this.effects.push({ createdAt: now, ...point, path: event.positions, label: ({ 'cold.freeze': '빙결', 'cold.shatter': '쇄빙', 'lightning.discharge': '방전', 'lightning.paralysis': '마비' } as Record<string, string>)[event.ruleId] ?? '', color: event.ruleId.startsWith('lightning.') ? '#ffe66d' : '#bde0fe' }); }
      else if (event.type === 'death') { const point = positions.get(event.targetId); if (point) this.effects.push({ createdAt: now, ...point, label: '처치', color: '#f2f2f2' }); }
      else if (event.type === 'cast_refused') { const point = positions.get(event.sourceId); if (point) this.effects.push({ createdAt: now, ...point, label: '사용 불가', color: '#ffc078' }); }
      else if (event.type === 'weapon_contact') {
        // A basic attack keeps its own marker; a crafted skill's swing is drawn by its blade (`BasicAttackAnimations`) and its thrust by the cone it covered.
        if (event.skillId === 'weapon_basic_attack') this.effects.push({ createdAt: now, x: event.aimPoint.x, y: event.aimPoint.y, label: '✦', color: '#bde0fe' });
        else this.addInstantContact(event, now);
      }
      else if (event.type === 'skill_impact') {
        if (event.shape.kind === 'SECTOR') this.sectorFlashes.push({ createdAt: now, x: event.center.x, y: event.center.y, facingRad: event.shape.facingRad, reachM: event.radiusM, angleDeg: event.shape.angleDeg });
        else this.skillImpacts.push({ createdAt: now, event });
      }
      else if (event.type === 'cast') this.skillCasts.push({ createdAt: now, event });
      // A projectile member is drawn as its own body in flight; an instant one is only seen as the jump.
      else if (event.type === 'chain_link' && event.instant) this.chainLinks.push({ createdAt: now, from: { ...event.from }, to: { ...event.to } });
      else if (event.type === 'chain_path' && event.instant) {
        for (let hop = 1; hop < event.points.length; hop += 1) this.chainLinks.push({ createdAt: now, from: { ...event.points[hop - 1]! }, to: { ...event.points[hop]! } });
      }
    }
    if (this.chainLinks.length > MAX_EFFECTS) this.chainLinks.splice(0, this.chainLinks.length - MAX_EFFECTS);
    if (this.skillImpacts.length > MAX_EFFECTS) this.skillImpacts.splice(0, this.skillImpacts.length - MAX_EFFECTS);
    if (this.sectorFlashes.length > MAX_EFFECTS) this.sectorFlashes.splice(0, this.sectorFlashes.length - MAX_EFFECTS);
    if (this.skillCasts.length > MAX_EFFECTS) this.skillCasts.splice(0, this.skillCasts.length - MAX_EFFECTS);
    if (this.effects.length > MAX_EFFECTS) this.effects.splice(0, this.effects.length - MAX_EFFECTS);
    if (this.blasts.length > MAX_EFFECTS) this.blasts.splice(0, this.blasts.length - MAX_EFFECTS);
  }

  /** A contact with no sweep (a thrust) lands at once: show the cone it covered. A sweep is drawn by its blade. */
  private addInstantContact(event: WeaponContactEvent, now: number): void {
    const geometry = event.geometry;
    if (event.durationS !== undefined || event.progress !== undefined || geometry?.reachM === undefined || geometry.angleDeg === undefined) return;
    this.sectorFlashes.push({ createdAt: now, x: event.origin.x, y: event.origin.y, facingRad: Math.atan2(event.forward.y, event.forward.x), reachM: geometry.reachM, angleDeg: geometry.angleDeg });
  }

  render(client: PredictedClient | undefined, field: GameFieldDescription | undefined, previews: RenderRangePreviews = {}, now = performance.now()): void {
    this.renderFrame({
      objects: client?.renderObjects(undefined, now) ?? [],
      localPlayerNetId: client?.playerNetId,
      serverTick: client?.serverTick,
    }, field, previews, now);
  }

  /** Snapshot-driven, connection-free entry point. This shares EXACTLY the
   * same drawing path with the online PredictedClient. The sandbox supplies
   * full production ObjectSnapshots + PresentationEvents at a fixed world
   * tick; it must never call the combat world from a render frame. */
  renderFrame(frame: RendererObjectFrame, field: GameFieldDescription | undefined, previews: RenderRangePreviews = {}, now = performance.now()): void {
    this.resize();
    // Camera and range previews must use the same presentation frame as actors.
    const objects = frame.objects;
    const local = objects.find((object): object is Extract<ObjectSnapshot, { kind: 'player' }> => object.kind === 'player' && object.netId === frame.localPlayerNetId);
    if (local) {
      const radius = field?.radiusM ?? 900; const halfW = this.viewport.cssWidth / (2 * this.camera.pixelsPerMetre); const halfH = this.viewport.cssHeight / (2 * this.camera.pixelsPerMetre);
      this.camera.x = Math.max(-radius + halfW, Math.min(radius - halfW, local.x));
      this.camera.y = Math.max(-radius + halfH, Math.min(radius - halfH, local.y));
    }
    this.aim = this.currentAim();
    this.monsters = objects.filter((value) => value.kind === 'monster' && value.alive);
    const ctx = this.context; ctx.setTransform(this.viewport.devicePixelRatio, 0, 0, this.viewport.devicePixelRatio, 0, 0);
    ctx.clearRect(0, 0, this.viewport.cssWidth, this.viewport.cssHeight); ctx.fillStyle = '#17231c'; ctx.fillRect(0, 0, this.viewport.cssWidth, this.viewport.cssHeight);
    if (field) this.drawField(field);
    if (local && previews.basic) this.drawRangePreview(local, previews.basic, basicAttackPreviewAngle(local, objects, this.aim, previews.basicAimMode ?? 'AUTO_TARGET'), false);
    this.skillAimMode = previews.skillAimMode ?? 'AUTO';
    if (local && previews.skill) this.drawRangePreview(local, previews.skill, skillPreviewAngle(local, objects, this.aim, this.skillAimMode), true);
    this.drawOrbitPaths(objects);
    for (const object of objects.filter((value) => value.kind === 'attack')) this.drawObject(object, local?.netId);
    for (const object of objects.filter((value) => value.kind === 'monster')) this.drawObject(object, local?.netId, frame.serverTick);
    for (const object of objects.filter((value) => value.kind === 'player')) this.drawObject(object, local?.netId);
    this.blasts = this.blasts.filter((blast) => now - blast.createdAt <= BLAST_EFFECT_LIFETIME_MS);
    for (const blast of this.blasts) this.drawBlast(blast, now);
    this.skillImpacts = this.skillImpacts.filter((flash) => now - flash.createdAt <= SKILL_FLASH_LIFETIME_MS);
    for (const flash of this.skillImpacts) this.drawSkillImpact(flash, now);
    this.sectorFlashes = this.sectorFlashes.filter((flash) => now - flash.createdAt <= SKILL_FLASH_LIFETIME_MS);
    for (const flash of this.sectorFlashes) this.drawSectorFlash(flash, now);
    this.skillCasts = this.skillCasts.filter((flash) => now - flash.createdAt <= SKILL_FLASH_LIFETIME_MS);
    for (const flash of this.skillCasts) this.drawSkillCast(flash, objects, now);
    this.chainLinks = this.chainLinks.filter((link) => now - link.createdAt <= SKILL_FLASH_LIFETIME_MS);
    for (const link of this.chainLinks) this.drawChainLink(link, now);
    this.effects = this.effects.filter((effect) => now - effect.createdAt <= EFFECT_LIFETIME_MS);
    for (const effect of this.effects) this.drawEffect(effect, now);
    if (this.aim) this.drawAim(this.aim);
    if (field) this.drawMinimap(field, objects, local?.netId);
  }

  private point(point: Point): Point { return worldToScreen(point, this.camera, this.viewport); }
  private circle(point: Point, radius: number, fill: string, stroke?: string): void { const p = this.point(point); const ctx = this.context; ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(2, radius * this.camera.pixelsPerMetre), 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill(); if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); } }
  private aimAngle(origin: Point): number { return this.aim ? Math.atan2(this.aim.y - origin.y, this.aim.x - origin.x) : 0; }
  private nearestMonster(from: Point): Point | undefined {
    let best: Point | undefined; let bestD = Infinity;
    for (const monster of this.monsters) { const d = Math.hypot(monster.x - from.x, monster.y - from.y); if (d < bestD) { bestD = d; best = monster; } }
    return best;
  }
  private drawRangePreview(origin: Point, preview: RangePreview, angle: number, active: boolean): void {
    const ctx = this.context; const p = this.point(origin); const scale = this.camera.pixelsPerMetre;
    const fill = active ? 'rgba(255, 193, 7, .20)' : 'rgba(116, 198, 157, .09)';
    const stroke = active ? 'rgba(255, 213, 79, .95)' : 'rgba(116, 198, 157, .60)';
    let label = p;
    ctx.save(); ctx.fillStyle = fill; ctx.strokeStyle = stroke; ctx.lineWidth = active ? 2 : 1;
    if (preview.kind === 'footprint') {
      ctx.restore();
      this.drawFootprint(origin, preview, angle, active);
      return;
    }
    if (preview.kind === 'sector') {
      const radius = preview.reachM * scale; const half = preview.angleDeg * Math.PI / 360;
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.arc(p.x, p.y, radius, angle - half, angle + half); ctx.closePath(); ctx.fill(); ctx.stroke();
      label = { x: p.x + Math.cos(angle) * radius, y: p.y + Math.sin(angle) * radius };
    } else if (preview.kind === 'corridor') {
      const length = preview.lengthM * scale; const width = Math.max(3, preview.widthM * scale);
      ctx.translate(p.x, p.y); ctx.rotate(angle); ctx.fillRect(0, -width / 2, length, width); ctx.strokeRect(0, -width / 2, length, width);
      ctx.setTransform(this.viewport.devicePixelRatio, 0, 0, this.viewport.devicePixelRatio, 0, 0);
      label = { x: p.x + Math.cos(angle) * length, y: p.y + Math.sin(angle) * length };
    } else if (preview.kind === 'ranged-basic') {
      const acquisitionRadius = preview.acquisitionRangeM * scale;
      const travelLength = preview.travelRangeM * scale;
      const projectileWidth = Math.max(3, preview.projectileWidthM * scale);
      ctx.setLineDash([6, 5]); ctx.beginPath(); ctx.arc(p.x, p.y, acquisitionRadius, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(angle); ctx.fillRect(0, -projectileWidth / 2, travelLength, projectileWidth); ctx.strokeRect(0, -projectileWidth / 2, travelLength, projectileWidth); ctx.restore();
      const endpoint = { x: origin.x + Math.cos(angle) * preview.travelRangeM, y: origin.y + Math.sin(angle) * preview.travelRangeM };
      if (preview.explosionRadiusM !== undefined) { const endpointScreen = this.point(endpoint); ctx.beginPath(); ctx.arc(endpointScreen.x, endpointScreen.y, preview.explosionRadiusM * scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
      ctx.fillStyle = active ? '#ffe082' : '#9ed8b8'; ctx.font = active ? 'bold 12px system-ui' : '11px system-ui'; ctx.textAlign = 'center';
      ctx.fillText(preview.labels.acquisition, p.x, p.y - acquisitionRadius - 7);
      ctx.fillText(preview.labels.travel, p.x + Math.cos(angle) * travelLength, p.y + Math.sin(angle) * travelLength - 7);
      ctx.restore(); return;
    } else {
      let center = origin;
      if (preview.center === 'nearest-enemy' && this.skillAimMode === 'MOUSE') {
        // A pointer-aimed slot sends the pointer as the ground point.
        center = this.aim ?? origin;
      } else if (preview.center === 'nearest-enemy') {
        // Where the server will put it: the nearest living monster, or at the caster for a zone with none; an instant circle with none is not cast.
        const nearest = this.nearestMonster(origin);
        if (nearest === undefined && !preview.persistent) { ctx.restore(); return; }
        center = nearest ?? origin;
      } else if (preview.center === 'aim' && this.aim) {
        const dx = this.aim.x - origin.x; const dy = this.aim.y - origin.y; const distance = Math.hypot(dx, dy);
        const limit = preview.acquisitionRangeM ?? distance; const factor = distance > limit && distance > 0 ? limit / distance : 1;
        center = { x: origin.x + dx * factor, y: origin.y + dy * factor };
        if (preview.acquisitionRangeM) { ctx.setLineDash([6, 5]); ctx.beginPath(); ctx.arc(p.x, p.y, preview.acquisitionRangeM * scale, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]); }
      }
      const c = this.point(center); ctx.beginPath(); ctx.arc(c.x, c.y, preview.radiusM * scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      label = { x: c.x + preview.radiusM * scale, y: c.y };
    }
    ctx.fillStyle = active ? '#ffe082' : '#9ed8b8'; ctx.font = active ? 'bold 12px system-ui' : '11px system-ui'; ctx.textAlign = 'center'; ctx.fillText(preview.label, label.x, label.y - 7); ctx.restore();
  }

  /**
   * Every member of a release at the pose the server will give it
   * (`resolveAttackPose`, the runtime's own transform), the blast it sets off
   * where it ends, and the circle an orbit's orbs travel. Repeats of one
   * footprint (a volley) are drawn once.
   */
  private drawFootprint(origin: Point, preview: FootprintPreview, aimRad: number, active: boolean): void {
    const drawn = new Set<string>();
    for (const member of preview.members) {
      const pose = memberPose({ origin, aimRad }, member);
      const key = [member.shape.kind, pose.origin.x.toFixed(6), pose.origin.y.toFixed(6), pose.directionRad.toFixed(6)].join(':');
      if (drawn.has(key)) continue;
      drawn.add(key);
      this.drawRangePreview(pose.origin, { ...member.shape, label: '' }, pose.directionRad, active);
      const blastRadiusM = member.blastRadiusM ?? member.splashRadiusM;
      if (blastRadiusM === undefined) continue;
      this.drawBlastReach(pose.origin, pose.directionRad, member.shape, blastRadiusM, active);
    }
    const ctx = this.context; const scale = this.camera.pixelsPerMetre;
    for (const orbit of preview.orbits) {
      const c = this.point(origin);
      ctx.save(); ctx.strokeStyle = active ? 'rgba(255, 213, 79, .95)' : 'rgba(116, 198, 157, .60)'; ctx.fillStyle = active ? 'rgba(255, 193, 7, .35)' : 'rgba(116, 198, 157, .20)';
      ctx.setLineDash([4, 5]); ctx.beginPath(); ctx.arc(c.x, c.y, orbit.radiusM * scale, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
      for (const phase of orbit.initialPhasesRad) {
        const orb = this.point(orbitPosition(orbit, origin, phase, 0));
        ctx.beginPath(); ctx.arc(orb.x, orb.y, orbit.orbRadiusM * scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      }
      ctx.restore();
    }
    const label = this.point(origin);
    ctx.save(); ctx.fillStyle = active ? '#ffe082' : '#9ed8b8'; ctx.font = active ? 'bold 12px system-ui' : '11px system-ui'; ctx.textAlign = 'center';
    ctx.fillText(preview.label, label.x, label.y - 24); ctx.restore();
  }

  /**
   * Where a blast set off by this member can land: its blast radius around any
   * point of the member's footprint, drawn as the footprint's outline thickened
   * by that radius, and the blast at the far end of its reach.
   */
  private drawBlastReach(origin: Point, angle: number, shape: ShapePreview, radiusM: number, active: boolean): void {
    const ctx = this.context; const p = this.point(origin); const scale = this.camera.pixelsPerMetre;
    if (shape.kind === 'circle') return;
    ctx.save(); ctx.lineJoin = 'round'; ctx.lineWidth = 2 * radiusM * scale;
    ctx.strokeStyle = active ? 'rgba(255, 112, 67, .16)' : 'rgba(255, 112, 67, .08)';
    ctx.beginPath();
    if (shape.kind === 'sector') {
      const half = shape.angleDeg * Math.PI / 360;
      ctx.moveTo(p.x, p.y); ctx.arc(p.x, p.y, shape.reachM * scale, angle - half, angle + half); ctx.closePath();
    } else {
      ctx.translate(p.x, p.y); ctx.rotate(angle); ctx.rect(0, -shape.widthM * scale / 2, shape.lengthM * scale, shape.widthM * scale);
    }
    ctx.stroke(); ctx.restore();
    const reachM = shape.kind === 'sector' ? shape.reachM : shape.lengthM;
    const end = { x: origin.x + Math.cos(angle) * reachM, y: origin.y + Math.sin(angle) * reachM };
    this.drawRangePreview(end, { kind: 'circle', center: 'caster', radiusM, label: '' }, 0, active);
  }

  /** The circle each orb of an orbit travels on, round its owner (or the fixed centre the server names). */
  private drawOrbitPaths(objects: readonly ObjectSnapshot[]): void {
    const owners = new Map(objects.map((object) => [object.netId, object]));
    const ctx = this.context; const drawn = new Set<string>();
    for (const object of objects) {
      if (object.kind !== 'attack' || object.presentationKind !== 'ORBIT' || object.orbitRadiusM === undefined) continue;
      const center = object.orbitCenter ?? owners.get(object.ownerNetId);
      if (center === undefined) continue;
      const key = `${object.ownerNetId}:${object.orbitRadiusM}:${center.x.toFixed(2)}:${center.y.toFixed(2)}`;
      if (drawn.has(key)) continue;
      drawn.add(key);
      const c = this.point(center);
      ctx.save(); ctx.strokeStyle = 'rgba(189, 224, 254, .35)'; ctx.lineWidth = 1; ctx.setLineDash([3, 6]);
      ctx.beginPath(); ctx.arc(c.x, c.y, object.orbitRadiusM * this.camera.pixelsPerMetre, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
    }
  }

  private drawSectorFlash(flash: SectorFlash, now: number): void {
    const age = Math.max(0, Math.min(1, (now - flash.createdAt) / SKILL_FLASH_LIFETIME_MS));
    const p = this.point(flash); const radius = flash.reachM * this.camera.pixelsPerMetre; const half = flash.angleDeg * Math.PI / 360;
    const ctx = this.context;
    ctx.save(); ctx.globalAlpha = 1 - age;
    ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.arc(p.x, p.y, radius, flash.facingRad - half, flash.facingRad + half); ctx.closePath();
    ctx.fillStyle = 'rgba(233, 236, 239, .16)'; ctx.fill(); ctx.strokeStyle = 'rgba(233, 236, 239, .9)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.restore();
  }

  private drawField(field: GameFieldDescription): void {
    const ctx = this.context; const center = this.point({ x: 0, y: 0 });
    const fieldRadiusPx = field.radiusM * this.camera.pixelsPerMetre;
    ctx.beginPath(); ctx.arc(center.x, center.y, fieldRadiusPx, 0, Math.PI * 2); ctx.fillStyle = '#243b2c'; ctx.fill();
    ctx.save(); ctx.beginPath(); ctx.arc(center.x, center.y, fieldRadiusPx, 0, Math.PI * 2); ctx.clip(); this.drawWorldGrid(); ctx.restore();
    ctx.beginPath(); ctx.arc(center.x, center.y, fieldRadiusPx, 0, Math.PI * 2); ctx.strokeStyle = '#8fb996'; ctx.lineWidth = 3; ctx.stroke();
    this.circle(field.rest, field.rest.radiusM, 'rgba(112, 193, 179, .25)', '#70c1b3');
    for (const landmark of field.landmarks) { const p = this.point(landmark); ctx.fillStyle = landmark.band === 'danger' ? '#f28482' : landmark.band === 'amber' ? '#f6bd60' : '#84a98c'; ctx.font = '12px system-ui'; ctx.fillText(landmark.name, p.x + 6, p.y - 6); ctx.fillRect(p.x - 2, p.y - 2, 4, 4); }
    // A camp is labelled only while its marker is on screen: a field holds hundreds of camps.
    for (const camp of field.camps) { const p = this.point(camp); if (p.x < -8 || p.y < -8 || p.x > this.viewport.cssWidth + 8 || p.y > this.viewport.cssHeight + 24) continue; ctx.fillStyle = camp.band === 'danger' ? '#f28482' : camp.band === 'amber' ? '#f6bd60' : '#84a98c'; ctx.font = '12px system-ui'; ctx.fillText(`${camp.monsterName} Lv${camp.level}`, p.x + 6, p.y - 6); ctx.fillRect(p.x - 2, p.y - 2, 4, 4); }
    for (const obstacle of field.terrain.obstacles) {
      ctx.fillStyle = 'rgba(12, 18, 14, .72)'; const shape = obstacle.shape;
      if (shape.type === 'circle') { const p = this.point(shape.center); ctx.beginPath(); ctx.arc(p.x, p.y, shape.radiusM * this.camera.pixelsPerMetre, 0, Math.PI * 2); ctx.fill(); }
      else { const a = this.point({ x: shape.minX, y: shape.minY }); const b = this.point({ x: shape.maxX, y: shape.maxY }); ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y); }
    }
  }

  private drawWorldGrid(): void {
    const ctx = this.context; const coordinates = visibleGridCoordinates(this.camera, this.viewport, WORLD_GRID_SPACING_M);
    ctx.beginPath();
    for (const x of coordinates.x) { const screenX = this.point({ x, y: this.camera.y }).x; ctx.moveTo(screenX, 0); ctx.lineTo(screenX, this.viewport.cssHeight); }
    for (const y of coordinates.y) { const screenY = this.point({ x: this.camera.x, y }).y; ctx.moveTo(0, screenY); ctx.lineTo(this.viewport.cssWidth, screenY); }
    ctx.strokeStyle = 'rgba(196, 224, 202, .12)'; ctx.lineWidth = 1; ctx.stroke();
  }

  /** The dash lane and its impact circle: the lane runs the charge's full distance during the wind-up and its remaining distance during the dash, and a charge with no presentation size keeps the old 55 px line. */
  private drawChargeTelegraph(monster: Extract<ObjectSnapshot, { kind: 'monster' }>, p: { x: number; y: number }): void {
    const ctx = this.context; const direction = monster.telegraph!.direction!; const charge = monster.attackPresentation?.charge;
    ctx.strokeStyle = '#ff6b6b'; ctx.fillStyle = 'rgba(255, 107, 107, .18)'; ctx.lineWidth = 3;
    if (!charge) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + direction.x * 55, p.y + direction.y * 55); ctx.stroke(); return; }
    const pixelsPerMetre = this.camera.pixelsPerMetre;
    const lengthM = monster.telegraph!.remainingDistanceM ?? charge.maxDistanceM;
    const end = { x: p.x + direction.x * lengthM * pixelsPerMetre, y: p.y + direction.y * lengthM * pixelsPerMetre };
    const radius = charge.impactRadiusM * pixelsPerMetre;
    ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(end.x, end.y); ctx.stroke();
    ctx.beginPath(); ctx.arc(end.x, end.y, radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  }

  private drawObject(object: ObjectSnapshot, localId: string | undefined, serverTick?: number): void {
    const ctx = this.context; const p = this.point(object);
    if (object.kind === 'attack') { if (!this.drawAttackAsset(object)) this.drawAttackFallback(object); return; }
    if (object.kind === 'monster' && object.telegraph?.direction) this.drawChargeTelegraph(object, p);
    const local = object.kind === 'player' && object.netId === localId;
    const inactive = object.kind === 'player' ? object.down : !object.alive;
    const look = object.kind === 'monster' ? monsterLook(object) : undefined;
    this.circle(object, look?.radiusM ?? PLAYER_VISUAL_RADIUS_M, look?.fill ?? (local ? '#4dabf7' : '#74c69d'), inactive ? '#777' : '#f8f9fa');
    // The bar is 30px wide on a default body and follows the body; a larger body lifts it clear of the circle.
    const bodyScale = look === undefined ? 1 : look.radiusM / MONSTER_VISUAL_RADIUS_M;
    const barHalf = 15 * bodyScale; const lift = look === undefined ? 0 : (look.radiusM - MONSTER_VISUAL_RADIUS_M) * this.camera.pixelsPerMetre;
    if (look?.ring) {
      ctx.strokeStyle = look.ring.color; ctx.lineWidth = 2;
      for (let ring = 1; ring <= look.ring.rings; ring += 1) { ctx.beginPath(); ctx.arc(p.x, p.y, look.radiusM * this.camera.pixelsPerMetre + 3 * ring, 0, Math.PI * 2); ctx.stroke(); }
    }
    const ratio = Math.max(0, Math.min(1, object.hp / Math.max(1, object.maxHp))); ctx.fillStyle = '#321'; ctx.fillRect(p.x - barHalf, p.y - 15 - lift, barHalf * 2, 4); ctx.fillStyle = ratio > .4 ? '#69db7c' : '#ff6b6b'; ctx.fillRect(p.x - barHalf, p.y - 15 - lift, barHalf * 2 * ratio, 4);
    if (object.kind === 'monster') {
      ctx.fillStyle = '#f8f9fa'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'center';
      ctx.fillText(`Lv. ${object.level}`, p.x, p.y - 22 - lift); ctx.textAlign = 'start';
      this.drawStatusChips(object, localId, serverTick, p.x, p.y - 36 - lift, barHalf, { y: p.y, radiusPx: (look?.radiusM ?? MONSTER_VISUAL_RADIUS_M) * this.camera.pixelsPerMetre });
    }
  }

  /**
   * The local player's own statuses on a monster, stacked upward above its level: a name and a thin lifetime bar each.
   * What other players left is one crowd-control row above them and a slow tint over the body.
   */
  private drawStatusChips(monster: Extract<ObjectSnapshot, { kind: 'monster' }>, localId: string | undefined, serverTick: number | undefined, x: number, baseline: number, barHalf: number, body: { readonly y: number; readonly radiusPx: number }): void {
    const chips = monsterStatusChips(monster.statuses, localId, serverTick);
    const others = statusSummaryMarks(monster.statusSummary);
    if (chips.length === 0 && others === null) return;
    const ctx = this.context; ctx.font = '11px system-ui'; ctx.textAlign = 'center';
    if (others !== null) {
      if (others.cc.length > 0) { ctx.fillStyle = '#ffa8a8'; ctx.fillText(others.cc.join(' '), x, baseline - chips.length * 15); }
      if (others.slow > 0) {
        ctx.fillStyle = `rgba(116, 192, 252, ${(0.15 + 0.35 * others.slow).toFixed(2)})`;
        ctx.beginPath(); ctx.arc(x, body.y, body.radiusPx, 0, Math.PI * 2); ctx.fill();
      }
    }
    chips.forEach((chip, index) => {
      const y = baseline - index * 15;
      ctx.fillStyle = '#ffe066'; ctx.fillText(chip.label, x, y);
      if (chip.remaining === null) return;
      ctx.fillStyle = 'rgba(0, 0, 0, .55)'; ctx.fillRect(x - barHalf, y + 2, barHalf * 2, 2);
      ctx.fillStyle = '#ffe066'; ctx.fillRect(x - barHalf, y + 2, barHalf * 2 * chip.remaining, 2);
    });
    ctx.textAlign = 'start';
  }

  /**
   * The procedural body of an attack without art: a projectile is a solid dot,
   * an orb a brighter ringed one, and a zone a faint dashed area that holds for
   * its lifetime (each pulse is its own `skill_impact`).
   */
  private drawAttackFallback(attack: Extract<ObjectSnapshot, { kind: 'attack' }>): void {
    if (attack.presentationKind === 'AREA') {
      const p = this.point(attack); const ctx = this.context;
      ctx.save(); ctx.beginPath(); ctx.arc(p.x, p.y, attack.radiusM * this.camera.pixelsPerMetre, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(189, 224, 254, .12)'; ctx.fill();
      ctx.setLineDash([8, 5]); ctx.strokeStyle = 'rgba(189, 224, 254, .8)'; ctx.lineWidth = 2; ctx.stroke(); ctx.restore();
    } else if (attack.presentationKind === 'ORBIT') {
      const p = this.point(attack); const ctx = this.context; const radius = attack.radiusM * this.camera.pixelsPerMetre;
      ctx.save(); ctx.beginPath(); ctx.arc(p.x, p.y, radius * 1.8, 0, Math.PI * 2); ctx.fillStyle = 'rgba(255, 224, 130, .25)'; ctx.fill();
      ctx.beginPath(); ctx.arc(p.x, p.y, radius, 0, Math.PI * 2); ctx.fillStyle = '#fff3bf'; ctx.fill();
      ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 2; ctx.stroke(); ctx.restore();
    } else this.circle(attack, attack.radiusM, 'rgba(189, 224, 254, .65)', '#bde0fe');
  }

  /** Body or area art for an attack, with a trail behind a moving body. False keeps the procedural circle. */
  private drawAttackAsset(attack: Extract<ObjectSnapshot, { kind: 'attack' }>): boolean {
    const stage = this.skillVisuals.profiles.stage(attack.visualProfileId, attack.stageId);
    if (stage === undefined) return false;
    const p = this.point(attack); const ppm = this.camera.pixelsPerMetre;
    const velocity = { x: attack.vx, y: attack.vy };
    if (attack.presentationKind === 'PROJECTILE' && (velocity.x !== 0 || velocity.y !== 0) && stage.TRAIL) {
      const trail = this.skillVisuals.assets(stage.TRAIL);
      if (trail) drawSkillAsset(this.context, trail, p, ppm, attack.radiusM, facingRotation(velocity));
    }
    const binding = attack.presentationKind === 'AREA' ? stage.AREA : stage.BODY;
    const asset = binding && this.skillVisuals.assets(binding);
    if (!asset) return false;
    return drawSkillAsset(this.context, asset, p, ppm, attack.radiusM, asset.spec.facing === 'VELOCITY' ? facingRotation(velocity) : 0);
  }

  /**
   * An impact shows the damaged circle whatever its profile: its art when
   * resolved, otherwise a neutral ring on the blast shockwave geometry, so no
   * skill reads as fire by default.
   */
  private drawSkillImpact({ createdAt, event }: SkillFlash<SkillImpactEvent>, now: number): void {
    const cause = event.shape.kind === 'CIRCLE' ? event.shape.cause : 'AREA';
    const binding = cause === 'PULSE' ? undefined : this.skillVisuals.profiles.stage(event.visualProfileId, event.stageId)?.IMPACT;
    const asset = binding && this.skillVisuals.assets(binding);
    const center = this.point(event.center);
    if (cause === 'PULSE') {
      // One tick of a zone: a ring that sweeps out to the zone's edge.
      const elapsed = now - createdAt; const age = Math.max(0, Math.min(1, elapsed / BLAST_EFFECT_LIFETIME_MS));
      if (elapsed > BLAST_EFFECT_LIFETIME_MS) return;
      const ctx = this.context; ctx.save(); ctx.globalAlpha = 1 - age;
      ctx.beginPath(); ctx.arc(center.x, center.y, Math.max(0.1, event.radiusM * age * this.camera.pixelsPerMetre), 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(189, 224, 254, .95)'; ctx.lineWidth = Math.max(1, 3 * (1 - age)); ctx.stroke(); ctx.restore();
      return;
    }
    if (asset) {
      const age = Math.max(0, Math.min(1, (now - createdAt) / SKILL_FLASH_LIFETIME_MS));
      if (drawSkillAsset(this.context, asset, center, this.camera.pixelsPerMetre, event.radiusM, 0, 1 - age)) return;
    }
    const elapsed = now - createdAt;
    if (elapsed > BLAST_EFFECT_LIFETIME_MS) return;
    // A detonation is the blast a hit sets off: the shockwave, not the neutral ring.
    if (cause === 'DETONATION') { this.drawBlast({ createdAt, tick: event.tick, attackKey: '', x: event.center.x, y: event.center.y, radiusM: event.radiusM }, now); return; }
    const age = Math.max(0, elapsed / BLAST_EFFECT_LIFETIME_MS);
    const ctx = this.context;
    ctx.save();
    ctx.globalAlpha = 1 - age;
    ctx.beginPath(); ctx.arc(center.x, center.y, Math.max(0.1, blastShockwaveRadiusM(event.radiusM, elapsed) * this.camera.pixelsPerMetre), 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(233, 236, 239, .12)'; ctx.fill();
    ctx.strokeStyle = 'rgba(233, 236, 239, .9)'; ctx.lineWidth = Math.max(1, 3 * (1 - age)); ctx.stroke();
    ctx.restore();
  }

  /** A short bowed arc between two enemies of a chain, fading over a flash's lifetime. */
  private drawChainLink(link: { readonly createdAt: number; readonly from: Point; readonly to: Point }, now: number): void {
    const age = Math.max(0, Math.min(1, (now - link.createdAt) / SKILL_FLASH_LIFETIME_MS));
    const from = this.point(link.from); const to = this.point(link.to);
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    // Bowed sideways by a fifth of its length, so a jump reads as an arc rather than a line.
    const bend = { x: -(to.y - from.y) / 5, y: (to.x - from.x) / 5 };
    const ctx = this.context;
    ctx.save(); ctx.globalAlpha = 1 - age;
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.quadraticCurveTo(mid.x + bend.x, mid.y + bend.y, to.x, to.y);
    ctx.strokeStyle = 'rgba(208, 235, 255, .95)'; ctx.lineWidth = Math.max(1, 3 * (1 - age)); ctx.stroke();
    ctx.restore();
  }

  /** Cast art follows the caster's presented position, turned toward the aim captured on acceptance. */
  private drawSkillCast({ createdAt, event }: SkillFlash<CastEvent>, objects: readonly ObjectSnapshot[], now: number): void {
    const binding = this.skillVisuals.profiles.cast(event.visualProfileId);
    const asset = binding && this.skillVisuals.assets(binding);
    const caster = asset && objects.find((object) => object.netId === event.sourceId);
    if (!asset || !caster) return;
    const age = Math.max(0, Math.min(1, (now - createdAt) / SKILL_FLASH_LIFETIME_MS));
    drawSkillAsset(this.context, asset, this.point(caster), this.camera.pixelsPerMetre, asset.spec.referenceRadiusM,
      asset.spec.facing === 'NONE' ? 0 : facingRotation(event.castForward), 1 - age);
  }

  private drawBlast(blast: BlastEffect, now: number): void {
    const age = Math.max(0, Math.min(1, (now - blast.createdAt) / BLAST_EFFECT_LIFETIME_MS));
    const p = this.point(blast); const ctx = this.context;
    const radius = blastShockwaveRadiusM(blast.radiusM, now - blast.createdAt) * this.camera.pixelsPerMetre;
    ctx.save();
    ctx.globalAlpha = 1 - age;
    ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(0.1, radius), 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 172, 64, .22)'; ctx.fill();
    ctx.strokeStyle = '#ffd166'; ctx.lineWidth = Math.max(1, 4 * (1 - age)); ctx.stroke();
    if (age < .25) {
      ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(2, blast.radiusM * this.camera.pixelsPerMetre * .12), 0, Math.PI * 2);
      ctx.fillStyle = '#fff3bf'; ctx.fill();
    }
    ctx.restore();
  }

  private drawEffect(effect: Effect, now: number): void { const p = this.point(effect); const t = (now - effect.createdAt) / EFFECT_LIFETIME_MS; const ctx = this.context; ctx.globalAlpha = 1 - t; if (effect.path?.length) { ctx.beginPath(); for (const [i, node] of effect.path.entries()) { const point = this.point(node); if (i === 0) ctx.moveTo(point.x, point.y); else ctx.lineTo(point.x, point.y); } ctx.strokeStyle = effect.color; ctx.stroke(); } ctx.fillStyle = effect.color; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center'; ctx.fillText(effect.label, p.x, p.y - 12 - t * 24); ctx.textAlign = 'start'; ctx.globalAlpha = 1; }
  private drawAim(point: Point): void { const p = this.point(point); const ctx = this.context; ctx.strokeStyle = 'rgba(255,255,255,.75)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI * 2); ctx.moveTo(p.x - 10, p.y); ctx.lineTo(p.x + 10, p.y); ctx.moveTo(p.x, p.y - 10); ctx.lineTo(p.x, p.y + 10); ctx.stroke(); }
  private drawMinimap(field: GameFieldDescription, objects: readonly ObjectSnapshot[], localId: string | undefined): void {
    const ctx = this.context; const size = 130; const left = this.viewport.cssWidth - size - 12; const top = 12; const scale = (size / 2 - 5) / field.radiusM;
    ctx.fillStyle = 'rgba(9, 15, 12, .78)'; ctx.fillRect(left, top, size, size); ctx.strokeStyle = '#8fb996'; ctx.strokeRect(left, top, size, size);
    for (const landmark of field.landmarks) { ctx.fillStyle = landmark.band === 'danger' ? '#f28482' : '#f6bd60'; ctx.fillRect(left + size / 2 + landmark.x * scale - 1, top + size / 2 + landmark.y * scale - 1, 3, 3); }
    for (const object of objects) { if (object.kind === 'attack') continue; ctx.fillStyle = object.kind === 'monster' ? '#e76f51' : object.netId === localId ? '#4dabf7' : '#74c69d'; ctx.fillRect(left + size / 2 + object.x * scale - 1, top + size / 2 + object.y * scale - 1, 3, 3); }
  }
}
